// v3.4.690 — إحصائيات الحجوزات: تبويبان (مشاريع الشركة / الحجوزات الخارجية) — عدد الحجوزات الشهري حسب الحالة،
// ما تمّ وما أُلغي وما هو تحت الإجراء، ومدة الإكمال (من تاريخ الحجز حتى أول تغيير للحالة إلى «تمت العمليه» في سجل التدقيق).
import React, { useState, useEffect, useMemo } from "react";
import { ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, BarChart } from "recharts";
import { reservations as resApi } from "../services/api";

const SCOPES = [
  { key: "company", label: "🏢 حجوزات مشاريع الشركة", hint: "الحجوزات المرتبطة بمطوّر له مشاريع في النظام" },
  { key: "external", label: "🌍 الحجوزات الخارجية", hint: "كل الحجوزات الأخرى (مطوّرون خارجيون)" },
];
const ST = [
  ["completed", "تمت العملية", "#16a34a"],
  ["pending", "تحت الإجراء", "#f59e0b"],
  ["failed", "لم تتم (ملغاة)", "#dc2626"],
  ["other", "أخرى (إعادة بيع/تنازل)", "#94a3b8"],
];
const MONTHS_AR = ["يناير", "فبراير", "مارس", "أبريل", "مايو", "يونيو", "يوليو", "أغسطس", "سبتمبر", "أكتوبر", "نوفمبر", "ديسمبر"];
const n0 = (v) => Number(v || 0);
const days = (v) => (v == null || v === "" ? "—" : `${Number(v).toLocaleString("en-US", { maximumFractionDigits: 1 })} يوم`);
const monthLabel = (k) => { const [y, m] = String(k).split("-"); return `${MONTHS_AR[Number(m) - 1] || m} ${y}`; };

export default function ReservationStatsPanel({ onClose }) {
  const [scope, setScope] = useState("company");
  const [year, setYear] = useState(String(new Date().getFullYear()));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    let alive = true; setLoading(true); setErr("");
    resApi.statistics({ scope, year: year === "all" ? "" : year })
      .then((r) => { if (alive) setData(r.data); })
      .catch((e) => { if (alive) setErr(e?.response?.data?.error || "تعذّر تحميل الإحصائيات"); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [scope, year]);

  const monthly = useMemo(() => (data?.monthly || []).map((m) => ({
    key: m.month, label: monthLabel(m.month).replace(/ \d{4}$/, year === "all" ? "" : "") + (year === "all" ? ` ${m.month.slice(2, 4)}'` : ""),
    "تمت العملية": n0(m.completed), "تحت الإجراء": n0(m.pending), "لم تتم (ملغاة)": n0(m.failed), "أخرى (إعادة بيع/تنازل)": n0(m.other),
    total: n0(m.total), avg_complete_days: m.avg_complete_days == null ? null : Number(m.avg_complete_days), n_timed: n0(m.n_timed),
    avg_cancel_days: m.avg_cancel_days == null ? null : Number(m.avg_cancel_days),
  })), [data, year]);
  const t = data?.totals || {};
  const years = data?.years || [];
  const buckets = data?.buckets || [];

  const card = (label, value, color, sub) => (
    <div style={{ background: "#fff", border: `1px solid ${color}33`, borderTop: `3px solid ${color}`, borderRadius: 10, padding: "10px 14px", minWidth: 130, flex: 1 }}>
      <div style={{ fontSize: 11.5, color: "#64748b", fontWeight: 600 }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 800, color, direction: "ltr", fontVariantNumeric: "tabular-nums", textAlign: "right" }}>{value}</div>
      {sub && <div style={{ fontSize: 10.5, color: "#94a3b8", marginTop: 2 }}>{sub}</div>}
    </div>
  );
  const chip = (on, label, onClick) => (
    <button key={label} type="button" onClick={onClick} style={{ padding: "5px 12px", borderRadius: 20, border: on ? "2px solid #166534" : "1px solid #e2e8f0", background: on ? "#dcfce7" : "#fff", color: on ? "#166534" : "#64748b", fontSize: 12, fontWeight: on ? 700 : 500, cursor: "pointer", fontFamily: "inherit" }}>{label}</button>
  );

  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.45)", zIndex: 9000, display: "flex", alignItems: "flex-start", justifyContent: "center", padding: "3vh 12px", overflowY: "auto" }}>
      <div onClick={(e) => e.stopPropagation()} dir="rtl" style={{ background: "#f8fafc", borderRadius: 14, width: "min(1180px, 100%)", boxShadow: "0 20px 60px rgba(0,0,0,0.3)", padding: 18, fontFamily: "inherit" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12, flexWrap: "wrap" }}>
          <div style={{ fontWeight: 800, fontSize: 17, color: "#0f172a" }}>📊 إحصائيات الحجوزات</div>
          <div style={{ fontSize: 12, color: "#64748b" }}>{SCOPES.find((s) => s.key === scope)?.hint}</div>
          <button type="button" onClick={onClose} style={{ marginInlineStart: "auto", border: "1px solid #cbd5e1", background: "#fff", borderRadius: 8, padding: "5px 12px", cursor: "pointer", fontFamily: "inherit" }}>✕ إغلاق</button>
        </div>

        {/* التبويبان */}
        <div style={{ display: "flex", gap: 0, borderBottom: "2px solid #e2e8f0", marginBottom: 12 }}>
          {SCOPES.map((s) => (
            <button key={s.key} type="button" onClick={() => setScope(s.key)} style={{ padding: "9px 18px", border: "none", borderBottom: scope === s.key ? "3px solid #166534" : "3px solid transparent", marginBottom: -2, background: "transparent", color: scope === s.key ? "#166534" : "#64748b", fontWeight: scope === s.key ? 800 : 600, fontSize: 14, cursor: "pointer", fontFamily: "inherit" }}>{s.label}</button>
          ))}
        </div>

        {/* السنوات */}
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center", marginBottom: 12 }}>
          <span style={{ fontSize: 11, color: "#64748b", fontWeight: 600 }}>📅</span>
          {chip(year === "all", "كل السنوات", () => setYear("all"))}
          {years.map((y) => chip(year === String(y), String(y), () => setYear(String(y))))}
          {loading && <span style={{ fontSize: 12, color: "#64748b" }}>⏳ جاري التحميل…</span>}
          {err && <span style={{ fontSize: 12, color: "#b91c1c" }}>{err}</span>}
        </div>

        {/* البطاقات */}
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 14 }}>
          {card("إجمالي الحجوزات", n0(t.total).toLocaleString("en-US"), "#1d4ed8")}
          {card("تمت العملية ✅", n0(t.completed).toLocaleString("en-US"), "#16a34a", t.total ? `${Math.round(n0(t.completed) * 100 / n0(t.total))}٪ من الإجمالي` : null)}
          {card("تحت الإجراء ⏳", n0(t.pending).toLocaleString("en-US"), "#d97706", t.pending_avg_age_days != null ? `متوسط عمر المفتوح: ${days(t.pending_avg_age_days)}` : null)}
          {card("لم تتم (ملغاة) ❌", n0(t.failed).toLocaleString("en-US"), "#dc2626", t.total ? `${Math.round(n0(t.failed) * 100 / n0(t.total))}٪ من الإجمالي` : null)}
          {card("متوسط مدة الإكمال", days(t.avg_complete_days), "#0f766e", t.n_timed ? `وسيط ${days(t.median_complete_days)} · محسوبة على ${n0(t.n_timed)} حجزًا مكتملًا له تاريخ إكمال مسجَّل` : "لا يوجد حجز مكتمل بتاريخ إكمال مسجَّل")}
          {card("متوسط المدة حتى الإلغاء", days(t.avg_cancel_days), "#9f1239", t.n_cancel_timed ? `على ${n0(t.n_cancel_timed)} حجزًا ملغى` : null)}
        </div>

        {/* الشهري حسب الحالة */}
        <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12, padding: 14, marginBottom: 12 }}>
          <div style={{ fontWeight: 800, fontSize: 14, marginBottom: 6 }}>📅 عدد الحجوزات الشهري حسب الحالة</div>
          {monthly.length === 0 ? <div style={{ color: "#64748b", fontSize: 13, padding: 20, textAlign: "center" }}>لا توجد حجوزات في هذا النطاق</div> : (
            <ResponsiveContainer width="100%" height={300}>
              <ComposedChart data={monthly} margin={{ top: 10, right: 10, left: 0, bottom: 10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="label" tick={{ fontSize: 11 }} interval={0} angle={monthly.length > 14 ? -35 : 0} height={monthly.length > 14 ? 60 : 30} textAnchor={monthly.length > 14 ? "end" : "middle"} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11 }} width={36} />
                <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} formatter={(v, name) => [v, name]} labelFormatter={(l, p) => `${l} — الإجمالي ${p?.[0]?.payload?.total ?? ""}`} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                {ST.map(([k, label, color]) => <Bar key={k} dataKey={label} stackId="m" fill={color} />)}
              </ComposedChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* مدة الإكمال */}
        <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 12, marginBottom: 12 }}>
          <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12, padding: 14 }}>
            <div style={{ fontWeight: 800, fontSize: 14, marginBottom: 2 }}>⏱ متوسط مدة الإكمال لكل شهر (بالأيام)</div>
            <div style={{ fontSize: 11, color: "#64748b", marginBottom: 6 }}>الشهر = شهر الحجز · المدة من تاريخ الحجز حتى تسجيل «تمت العملية» في النظام</div>
            {monthly.some((m) => m.avg_complete_days != null) ? (
              <ResponsiveContainer width="100%" height={220}>
                <ComposedChart data={monthly} margin={{ top: 10, right: 10, left: 0, bottom: 10 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="label" tick={{ fontSize: 11 }} interval={monthly.length > 14 ? Math.floor(monthly.length / 12) : 0} />
                  <YAxis tick={{ fontSize: 11 }} width={36} />
                  <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} formatter={(v, name, p) => [name === "الإكمال" ? `${v} يوم (${p.payload.n_timed} حجز)` : `${v} يوم`, name]} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Line type="monotone" dataKey="avg_complete_days" name="الإكمال" stroke="#0f766e" strokeWidth={2.5} dot={{ r: 3 }} connectNulls />
                  <Line type="monotone" dataKey="avg_cancel_days" name="الإلغاء" stroke="#9f1239" strokeWidth={1.5} strokeDasharray="5 3" dot={{ r: 2 }} connectNulls />
                </ComposedChart>
              </ResponsiveContainer>
            ) : <div style={{ color: "#64748b", fontSize: 13, padding: 20, textAlign: "center" }}>لا توجد مدد إكمال مسجَّلة في هذا النطاق</div>}
          </div>
          <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12, padding: 14 }}>
            <div style={{ fontWeight: 800, fontSize: 14, marginBottom: 6 }}>📦 توزيع مدة الإكمال</div>
            {buckets.length ? (
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={buckets} margin={{ top: 10, right: 10, left: 0, bottom: 10 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="label" tick={{ fontSize: 11 }} interval={0} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 11 }} width={30} />
                  <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} formatter={(v) => [`${v} حجز`, "العدد"]} />
                  <Bar dataKey="n" fill="#0f766e" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : <div style={{ color: "#64748b", fontSize: 13, padding: 20, textAlign: "center" }}>—</div>}
          </div>
        </div>

        {/* الجدول الشهري */}
        {monthly.length > 0 && (
          <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12, padding: 14, overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12.5 }}>
              <thead><tr style={{ background: "#f1f5f9" }}>{["الشهر", "الإجمالي", "تمت", "تحت الإجراء", "لم تتم", "أخرى", "نسبة الإتمام", "متوسط الإكمال", "متوسط الإلغاء"].map((h) => <th key={h} style={{ padding: "7px 8px", textAlign: "right", fontWeight: 700, color: "#334155", borderBottom: "1px solid #e2e8f0", whiteSpace: "nowrap" }}>{h}</th>)}</tr></thead>
              <tbody>
                {monthly.map((m) => (
                  <tr key={m.key} style={{ borderBottom: "1px solid #f1f5f9" }}>
                    <td style={{ padding: "6px 8px", fontWeight: 700 }}>{monthLabel(m.key)}</td>
                    <td style={{ padding: "6px 8px", direction: "ltr", textAlign: "right" }}>{m.total}</td>
                    <td style={{ padding: "6px 8px", color: "#16a34a", fontWeight: 700 }}>{m["تمت العملية"]}</td>
                    <td style={{ padding: "6px 8px", color: "#d97706", fontWeight: 700 }}>{m["تحت الإجراء"]}</td>
                    <td style={{ padding: "6px 8px", color: "#dc2626", fontWeight: 700 }}>{m["لم تتم (ملغاة)"]}</td>
                    <td style={{ padding: "6px 8px", color: "#64748b" }}>{m["أخرى (إعادة بيع/تنازل)"]}</td>
                    <td style={{ padding: "6px 8px" }}>{m.total ? `${Math.round(m["تمت العملية"] * 100 / m.total)}٪` : "—"}</td>
                    <td style={{ padding: "6px 8px" }}>{m.avg_complete_days == null ? "—" : `${days(m.avg_complete_days)} (${m.n_timed})`}</td>
                    <td style={{ padding: "6px 8px" }}>{days(m.avg_cancel_days)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
